import styled, { CSSObject } from '@emotion/styled';
import { useState } from 'react';
import * as React from 'react';
import LazyLoad, { LazyLoadProps } from 'react-lazyload';
import { Transition, TransitionStatus } from 'react-transition-group';

import { mq } from '@/utility/mediaQuery';

import Skeleton from '../Skeleton';

export type MediaSet = {
  srcSet: string;
  media?: string;
  type?: string;
};
export interface ImageProps extends Omit<LazyLoadProps, 'height' | 'width'> {
  width?: CSSObject['width'];
  height?: CSSObject['height'];
  aspectRatio?: number | number[];
  srcSet?: string;
  mediaSet?: MediaSet[];
  alt?: string;
}

const getHeight = (props: ImageProps) => {
  if (props.aspectRatio) {
    return typeof props.aspectRatio === 'number'
      ? `${props.aspectRatio * 100}%`
      : props.aspectRatio.map(ar => `${ar * 100}%`);
  }
  return props.height || '';
};

const Wrapper = styled.div(
  (props: Pick<ImageProps, 'width' | 'height' | 'aspectRatio'>) =>
    mq({
      width: props.width || '100%',
      height: props.height || '100%',
      paddingTop: getHeight(props),
      position: 'relative',
      '& img': {
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        objectFit: 'contain',
      },
    }),
);

export const Dummy = styled(Skeleton)(
  (props: { width?: CSSObject['width']; height?: CSSObject['height'] }) =>
    mq({
      position: 'absolute !important',
      top: 0,
      left: 0,
      width: props.width || '100%',
      height: props.height || '100%',
    }),
);

const duration = 200;

const defaultStyle = {
  transition: `opacity ${duration}ms ease-in-out`,
  opacity: 0,
};

const transitionStyles = {
  entering: { opacity: 1 },
  entered: { opacity: 1 },
} as { [key in TransitionStatus]: React.CSSProperties };

const Image: React.FC<ImageProps> = ({
  width,
  height,
  aspectRatio,
  srcSet,
  mediaSet,
  alt,
  overflow,
  scrollContainer,
}) => {
  const [loaded, setLoaded] = useState<boolean>(false);

  return (
    <Wrapper
      width={width}
      height={height}
      aspectRatio={aspectRatio}
      data-testid="lazy-image-wrapper"
    >
      <LazyLoad
        height="100%"
        once
        placeholder={<Dummy />}
        overflow={overflow}
        scrollContainer={scrollContainer}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
      >
        <Transition in={loaded} timeout={duration}>
          {state => (
            <div
              style={{
                ...defaultStyle,
                ...transitionStyles[state],
              }}
            >
              <picture>
                {mediaSet &&
                  mediaSet.map((m, i) => (
                    <source
                      key={`media-${i}`}
                      srcSet={m.srcSet}
                      media={m.media}
                      type={m.type}
                    />
                  ))}
                <img srcSet={srcSet} alt={alt} onLoad={() => setLoaded(true)} />
              </picture>
            </div>
          )}
        </Transition>
      </LazyLoad>
    </Wrapper>
  );
};

export default Image;
